import { names, vocabularies, VocabularyName } from './gen_vocabulary';
import { getLanguageFromLocale, Language } from '../Intl/utils';
import { SelectItem } from 'primereact/selectitem';
import { MultiAutocompleteCollection } from '../pages/publig/types';

export function getVocabularyNames(): VocabularyName[] {
  return names as VocabularyName[];
}

function _buildVocabularyNameRaw(vocab: string, language: string, partition: string = ''): string {
  return `${vocab}_${language}${partition.length ? '_' + partition : ''}`;
}

function _buildVocabularyName(vocab: string, language: string, partition: string = ''): VocabularyName {
  const name: string = _buildVocabularyNameRaw(vocab, language, partition);
  if (!names.find((n: string): boolean => n === name)) {
    throw new Error(`Unrecognized vocabulary name "${name}"`);
  }
  return name as VocabularyName;
}

export function getVocabularyRaw(vocabName: VocabularyName): Map<string, string> {
  return vocabularies[vocabName];
}

export function getVocabularyAsArray(vocab: string, language: string, partition: string = ''): MultiAutocompleteCollection[] {
  return Array.from(getVocabulary(vocab, language, partition)).map(([abbr, name]: [string, string]): MultiAutocompleteCollection => ({
    abbr,
    name,
  }));
}

/**
 *
 * @param vocab
 * @param language
 * @param partition "*" => Vereinigung aller Partitionen
 */
export function getVocabulary(vocab: string, language: string, partition: string = ''): Map<string, string> {
  const baseName: string = _buildVocabularyNameRaw(vocab, language);

  if (partition !== '*') {
    let map: Map<string, string> = new Map<string, string>();
    // Setze map = die Werte der generic-Parition
    if (
      partition &&
      partition.length > 0 &&
      getVocabularyNames().filter((n: VocabularyName): boolean => n === baseName + '_generic').length === 1
    ) {
      map = new Map<string, string>([...getVocabularyRaw(_buildVocabularyName(vocab, language, 'generic'))]);
    }
    // Wenn es die angegebene Partition gibt, ersetzte die Map durch die Werte der angegebenen Partition
    if (
      partition &&
      partition.length > 0 &&
      getVocabularyNames().filter((n: VocabularyName): boolean => n === baseName + '_' + partition).length === 1
    ) {
      map = new Map<string, string>([...getVocabularyRaw(_buildVocabularyName(vocab, language, partition))]);
    }
    // Wenn map noch leer ist, ersetzte die Map durch die Werte
    if ([...map].length < 1 && getVocabularyNames().filter((n: VocabularyName): boolean => n === baseName).length === 1) {
      map = new Map<string, string>([...getVocabularyRaw(_buildVocabularyName(vocab, language, ''))]);
    }
    return map;
  }

  const vocabNames: VocabularyName[] = getVocabularyNames().filter((n: VocabularyName): boolean => n === baseName || n.startsWith(baseName + '_'));
  return vocabNames
    .map((n: VocabularyName): Map<string, string> => getVocabularyRaw(n))
    .reduce(
      (a: Map<string, string>, v: Map<string, string>): Map<string, string> => new Map([...a, ...v]),
      new Map<string, string>(),
    );
}

export function getVocabularyOptions(locale: string, vocab: string, partition: string = ''): SelectItem[] {
  const lang: string = getLanguageFromLocale(locale).toLowerCase();
  return Array.from(getVocabulary(vocab, lang, partition), ([value, label]) => ({label, value}));
}

export function getNotationRaw(vocabName: VocabularyName, key: string): string {
  const vo: Map<string, string> = getVocabularyRaw(vocabName);
  const value: string = vo.get(key);
  if (undefined === value) {
    throw new Error(`Unrecognized key "${key}" in vocabulary`);
  }

  return value;
}

/**
 *
 * @param vocab
 * @param key
 * @param locale
 * @param partition "*" => Vereinigung aller Partitionen
 */
export function getNotation(vocab: string, key: string, locale: string, partition: string = ''): string {
  const language: Language = locale.split('-', 2)[0] as Language;
  const vo: Map<string, string> = getVocabulary(vocab, language, partition);
  const value: string = vo.get(key);
  if (undefined === value) {
    console.error(`Unrecognized key "${key}" in vocabulary "${vocab}"`);
    return key;
  }

  return value;
}
